/**
 * RC-152c — Field-type registry shared across the Form Designer
 * surfaces.
 *
 * Single source of truth for "what types can a form field be, what
 * are its operator-facing labels, and what config properties does
 * each type support." The FieldPalette renders one entry per
 * registry item; the FieldInspector reads the {@link FIELD_PROPS}
 * map to know which input controls to render for the selected
 * field; the FormCanvas reads {@link DEFAULTS_BY_TYPE} when
 * appending a new field so the operator gets a sensible starting
 * config rather than an empty object.
 *
 * The output JSON shape — what gets persisted into a workflow's
 * {@code form_task.config.formSchema.fields[]} — is exactly the
 * shape the existing runtime (CanvasDAGWorkflowImpl) and the
 * existing Approval Center renderer consume. No new fields, no
 * renames. The designer is purely an authoring surface; the
 * downstream contract is unchanged. See
 * docs/design/FORM_TASK_EVOLUTION.md.
 *
 * Categories mirror the design-doc mockup palette (Inputs / Choice
 * / File / Display) so when c-3 adds {@code type:"display"} and
 * {@code type:"section_break"} the registry grows by a single
 * category block rather than a structural change.
 */

/**
 * Field-type registry.
 *
 * Order in the array drives palette render order. Keep "common
 * first" ordering — text > textarea > number is more useful than
 * alphabetical because operators reach for short-text fields
 * most often.
 *
 * Each entry:
 *   value      — the on-the-wire `type` string. NEVER renamed; the
 *                runtime keys off this exact string.
 *   label      — operator-facing palette label.
 *   icon       — lucide-react icon name (string; consumer imports
 *                the icon by name to keep this module ESM-pure).
 *   category   — grouping for the palette UI.
 *   helpText   — short tooltip for the palette item.
 */
export const FIELD_TYPES = [
  { value: 'text',         label: 'Text',          icon: 'Type',        category: 'Inputs',  helpText: 'Single-line text input.' },
  { value: 'textarea',     label: 'Textarea',      icon: 'AlignLeft',   category: 'Inputs',  helpText: 'Multi-line text input.' },
  { value: 'number',       label: 'Number',        icon: 'Hash',        category: 'Inputs',  helpText: 'Numeric input with optional min / max / step.' },
  { value: 'date',         label: 'Date',          icon: 'Calendar',    category: 'Inputs',  helpText: 'Calendar date picker.' },
  { value: 'datetime',     label: 'Datetime',      icon: 'Clock',       category: 'Inputs',  helpText: 'Date + time picker.' },
  { value: 'select',       label: 'Select',        icon: 'ChevronDown', category: 'Choice',  helpText: 'Single-choice dropdown.' },
  { value: 'multi-select', label: 'Multi-select',  icon: 'ListChecks',  category: 'Choice',  helpText: 'Multiple-choice checkbox group.' },
  { value: 'checkbox',     label: 'Checkbox',      icon: 'Check',       category: 'Choice',  helpText: 'Single Yes / No checkbox.' },
  { value: 'file',         label: 'File upload',   icon: 'Upload',      category: 'File',    helpText: 'File attachment (RC-149 redacted in downstream interpolation by default).' },
];

/** Category order for the palette section headers. */
export const FIELD_CATEGORIES = ['Inputs', 'Choice', 'File'];

/**
 * Properties surface per field type. Each entry tells the
 * FieldInspector which property controls to render for the
 * selected field — beyond the always-present name / label /
 * required / helpText trio.
 *
 * Property descriptors:
 *   key         — the on-the-wire property name (matches what the
 *                 runtime + Approval Center already consume).
 *   label       — operator-facing label in the inspector.
 *   control     — 'text' | 'number' | 'textarea' | 'options' |
 *                 'string-array' | 'date-bound' | 'datetime-bound'
 *                 — which inspector control to render.
 *
 *                 'date-bound' / 'datetime-bound' (RC-152c-2a-datetz)
 *                 render a dual-mode editor: Absolute (calendar +
 *                 time + timezone) or Relative (now+1h / now-1d
 *                 expression). The on-the-wire string is preserved
 *                 verbatim either way; the runtime + Approval
 *                 Center already tolerate both shapes.
 *   placeholder — optional input placeholder.
 *   helpText    — optional one-line hint shown under the field.
 */
export const FIELD_PROPS = {
  text: [
    { key: 'placeholder', label: 'Placeholder',          control: 'text' },
    { key: 'pattern',     label: 'Pattern (regex)',      control: 'text',
      placeholder: '^CLM-[0-9]{8}$',
      helpText: 'Operator input must match this regular expression.' },
    { key: 'minLength',   label: 'Minimum length',       control: 'number' },
    { key: 'maxLength',   label: 'Maximum length',       control: 'number' },
  ],
  textarea: [
    { key: 'placeholder', label: 'Placeholder',          control: 'text' },
    { key: 'rows',        label: 'Rows (height)',        control: 'number',
      helpText: 'Default 6. Increase for longer free-text answers.' },
    { key: 'minLength',   label: 'Minimum length',       control: 'number' },
    { key: 'maxLength',   label: 'Maximum length',       control: 'number' },
  ],
  number: [
    { key: 'placeholder', label: 'Placeholder',          control: 'text' },
    { key: 'min',         label: 'Minimum value',        control: 'number' },
    { key: 'max',         label: 'Maximum value',        control: 'number' },
    { key: 'step',        label: 'Step',                 control: 'number',
      helpText: 'Increment per arrow click. Default 1.' },
  ],
  date: [
    { key: 'min',         label: 'Earliest date',        control: 'date-bound',
      helpText: 'Calendar picker — or switch to a relative expression like now or now+7d.' },
    { key: 'max',         label: 'Latest date',          control: 'date-bound',
      helpText: 'Calendar picker — or switch to a relative expression like now+30d.' },
  ],
  datetime: [
    { key: 'min',         label: 'Earliest datetime',    control: 'datetime-bound',
      helpText: 'Calendar + time + timezone — or switch to a relative expression like now+1h.' },
    { key: 'max',         label: 'Latest datetime',      control: 'datetime-bound',
      helpText: 'Calendar + time + timezone — or switch to a relative expression like now+1d.' },
  ],
  select: [
    { key: 'options',     label: 'Options',              control: 'options',
      helpText: 'Operator picks one. Each option has a value and a display label.' },
  ],
  'multi-select': [
    { key: 'options',     label: 'Options',              control: 'options',
      helpText: 'Operator picks any number. Each option has a value and a display label.' },
  ],
  checkbox: [
    // No additional properties — name / label / required cover the field.
  ],
  file: [
    { key: 'acceptedTypes', label: 'Accepted MIME types', control: 'string-array',
      placeholder: 'application/pdf, image/jpeg, image/png',
      helpText: 'Comma-separated. UploadGuard rejects everything outside the list (server-side magic-byte sniff).' },
    { key: 'maxSizeBytes',  label: 'Max size (bytes)',    control: 'number',
      helpText: 'Default 25 MiB. Larger uploads fail with a clear error.' },
  ],
};

/**
 * Sensible defaults applied when the operator clicks a palette
 * item to append a new field. Generates a unique-ish name from
 * the type ({@code newField_text_1}) — operator renames in the
 * inspector after.
 *
 * The `name` is incremented at append time (see FormCanvas) to
 * avoid collisions with existing fields.
 */
export const DEFAULTS_BY_TYPE = {
  text:          { type: 'text',         label: 'Text field',          required: false },
  textarea:      { type: 'textarea',     label: 'Textarea',             required: false, rows: 6 },
  number:        { type: 'number',       label: 'Number',               required: false },
  date:          { type: 'date',         label: 'Date',                 required: false },
  datetime:      { type: 'datetime',     label: 'Datetime',             required: false },
  select:        { type: 'select',       label: 'Select one',           required: false,
                   options: [{ value: 'option_a', label: 'Option A' }, { value: 'option_b', label: 'Option B' }] },
  'multi-select':{ type: 'multi-select', label: 'Select many',          required: false,
                   options: [{ value: 'option_a', label: 'Option A' }, { value: 'option_b', label: 'Option B' }] },
  checkbox:      { type: 'checkbox',     label: 'Confirm',              required: false },
  file:          { type: 'file',         label: 'Upload file',          required: false,
                   acceptedTypes: ['application/pdf', 'image/jpeg', 'image/png'],
                   maxSizeBytes: 10 * 1024 * 1024 },
};

/**
 * Generate a unique field name for a new field of the given type.
 * Walks the existing fields[] looking for the lowest-numbered
 * available suffix so the operator gets `newField_text_1` then
 * `newField_text_2` rather than colliding on `newField_text_1`.
 */
export function nextFieldName(type, existingFields) {
  const existing = new Set(
    (existingFields || []).map((f) => (f && f.name) || '').filter(Boolean),
  );
  let i = 1;
  while (existing.has(`newField_${type}_${i}`)) i++;
  return `newField_${type}_${i}`;
}
